import React, { useEffect } from "react";
import { useParams, withRouter, useHistory, Redirect } from "react-router-dom";
import EventAside from "../navigation/eventAside";
import { getAllEvents, deleteEvent, getEventById, updateEvent, createEvent, deselectEvent, getEventSharedWithMe } from "../../redux/actions/events";
import { getUser } from "../../redux/actions/users";
import EventBasicData from "../../pages/events/event/eventBasicData";
import { esqueletoAlta } from "./eventForm";
import EventStats from "../../pages/events/event/eventStats";
import EventPhotos from "../../pages/events/event/eventPhotos";
import { connect } from "react-redux";
import EventPricing from "../../pages/events/event/eventPricing";
import { useTranslation } from "react-i18next";
import Modal from "../generic/modal";
import EventSharedWith from "../../pages/events/event/eventSharedWith";
import EventPhotosGallery from "../../pages/events/event/eventPhotosGallery";
import EventPayments, { ESQUELETO_PAGOS } from "../../pages/events/event/eventPayments";
import EventCoupons from "../../pages/events/event/eventCoupons";
import EventWatermark from "../../pages/events/event/eventWatermark";
import EventPublicGallery from "../../pages/events/event/eventPublicGallery";
import EventSharedSummary from "../../pages/events/event/eventSharedSummary";
import { RevSkeleton } from "../ds";

const GUEST_MODES = ["shared-photo", "shared-gallery", "shared-summary"];

// Forma del esqueleto de primera carga por pestaña: el cuerpo de lo que se está pidiendo.
// Los recuentos son los del estado cargado de cada pantalla (cards reales, miniaturas =
// `pageSize` por defecto, filas = límite por defecto de Pagos); las clases de pantalla
// llegan del propio módulo de la pantalla, nunca escritas aquí.
const CUERPOS = {
    basic: { cuerpo: "cards", main: 2, aside: 1 },
    photos: { cuerpo: "cards", main: 2, aside: 2 },
    "shared-photo": { cuerpo: "cards", main: 2, aside: 1 },
    pricing: { cuerpo: "cards", main: 2, aside: 2 },
    shared: { cuerpo: "cards", main: 2, aside: 1 },
    coupons: { cuerpo: "cards", main: 2, aside: 2 },
    watermark: { cuerpo: "cards", main: 2, aside: 2 },
    "public-gallery": { cuerpo: "cards", main: 3, aside: 0 },
    stats: { cuerpo: "cards", main: 5, aside: 0 },
    "shared-summary": { cuerpo: "cards", main: 1, aside: 1 },
    gallery: { cuerpo: "galeria", miniaturas: 12, aside: 1 },
    "shared-gallery": { cuerpo: "galeria", miniaturas: 12, aside: 0 },
    payments: { cuerpo: "tabla", toolbar: true, ...ESQUELETO_PAGOS },
};

const EventContainer = (props) => {
    let { id, mode } = useParams();
    const { t } = useTranslation();
    const history = useHistory();
    const guest = GUEST_MODES.includes(mode);
    const sharedItem = props.sharedWithMe?.find((item) => item.revopicEvent?.id === id);
    const sharedEvent = sharedItem?.revopicEvent;

    useEffect(() => {
        if (!props.user) props.getUser(); 
        if (props.events.length === 0) props.getAllEvents();
    }, []); // eslint-disable-line

    useEffect(() => {
        if (!props.user) props.getUser(); 
        if (props.events.length > 0 && id && (!props.selectedEvent || props.selectedEvent.id !== id)) {
            props.getEventById(id);
        }
    }, [props.events, id]); // eslint-disable-line

    useEffect(() => {
        if (guest && (props.sharedWithMe?.length ?? 0) === 0) props.getEventSharedWithMe();
    }, [guest]); // eslint-disable-line

    // Primera carga de la página: no hay un flag propio, se deriva de lo que ya hay en el
    // store. El gate del usuario va aparte del gate del evento: con el evento ya cargado y
    // el usuario todavía en vuelo, `props.currency` es undefined y la pestaña de Precios
    // pintaría su modal de «sin moneda». Guardar (Datos básicos, Precios, Compartir) sube
    // el mismo `loading` pero deja `eventoListo` en true: el cuerpo no se desmonta.
    const usuarioListo = !!props.user?.id;
    const eventoListo = guest ? sharedEvent?.id === id : props.selectedEvent?.id === id;
    const cargandoPagina = !!id && (
        (props.loadingUser && !usuarioListo)
        || (props.loading && !eventoListo)
        || (guest && props.loadingSharedWithMe && !eventoListo)
    );
    // Alta: el gate del evento es la lista de eventos, que el contenedor pide al montar.
    // Con `props.loading` a secas el esqueleto taparía también el POST del botón «Crear»,
    // que sube el mismo flag; `eventsFetched` solo es falso hasta que vuelve el primer
    // GET (y una llamada resuelta con error deja de subir `loading`, así que la guarda se
    // resuelve también por el camino de error).
    const cargandoAlta = !id && (
        (props.loadingUser && !usuarioListo)
        || (props.loading && !props.eventsFetched)
    );

    return (
        <main className="rev-page">
            <EventAside eventId={id} mode={mode} guest={guest} sharedEvent={sharedEvent} cargando={cargandoPagina} />
            <div className="rev-page__content">
                {cargandoAlta ?
                    <>
                        <h1 className="rev-page-title">{t('event.new.title')}</h1>
                        <RevSkeleton.Page
                            forma="form"
                            label={t('event.photos.loading')}
                            {...esqueletoAlta(t)} />
                    </> :
                    cargandoPagina ?
                    <RevSkeleton.Page
                        forma="detalle"
                        label={t('event.photos.loading')}
                        {...(CUERPOS[mode] || CUERPOS.basic)} /> :
                    <>
                        {mode === "basic" || !id ?
                            <EventBasicData
                                id={id}
                                selectedEvent={props?.selectedEvent}
                                loading={props.loading}
                                getEventById={props.getEventById}
                                deselectEvent={props.deselectEvent}
                                createEvent={props.createEvent}
                                updateEvent={props.updateEvent}
                                deleteEvent={props.deleteEvent}
                            /> : null}
                        {/* Patrocinadores vive ahora dentro de la pestaña Galería pública. */}
                        {id && mode === "sponsors" ?
                            <Redirect to={"/events/" + id + "/public-gallery"} /> : null}
                        {id && mode !== "sponsors" && !Object.prototype.hasOwnProperty.call(CUERPOS, mode) ?
                            <Redirect to={"/events/" + id + "/basic"} /> : null}
                        {mode === "payments" ?
                            <EventPayments eventCode={props.selectedEvent?.code} /> : null}
                        {mode === "coupons" ?
                            <EventCoupons eventId={id} selectedEvent={props?.selectedEvent} /> : null}
                        {mode === "watermark" ?
                            <EventWatermark eventId={id} /> : null}
                        {mode === "public-gallery" ?
                            <EventPublicGallery eventId={id} selectedEvent={props.selectedEvent} user={props.user} /> : null}
                        {mode === "stats" ?
                            <EventStats eventId={id} /> : null}
                        {mode === "shared" ?
                            <EventSharedWith event={props?.selectedEvent} user={props?.user}/> : null}
                        {mode === "photos" || mode === "shared-photo" ?
                            <EventPhotos eventId={id} /> : null}
                        {mode === "gallery" || mode === "shared-gallery" ?
                            <EventPhotosGallery eventId={id} guest={mode === "shared-gallery"} sharedEvent={sharedEvent} /> : null}
                        {mode === "shared-summary" ?
                            <EventSharedSummary eventId={id} /> : null}
                        {mode === "pricing" && props.user?.currency
                            ? <EventPricing eventId={id}
                                currency={props?.currency}
                                selectedEvent={props?.selectedEvent} />
                            : (mode === "pricing" && !props.currency
                                ? <Modal
                                    title={t('No currency in account setting')}
                                    message={t('Please go to your account settings and set your currency in order to set the pricing for your event')}
                                    showConfirmButton={true}
                                    showCancelButton={true}
                                    cancelButtonText={t('Cancel')}
                                    confirmButtonText={t('Go to My Account')}
                                    onCancel={() => { history.push('/events/' + id + '/basic') }}
                                    onConfirm={() => { history.push('/account/photo-sale/home') }}
                                    loading={props.loading}
                                />
                                : null)}
                    </>}
            </div>
        </main>
    );
};

const mapStateToProps = state => ({
    user: state.usersReducer.user,
    loadingUser: state.usersReducer.loading,
    events: state.eventsReducer.events,
    sharedWithMe: state.eventsReducer.eventSharedWithMe,
    currency: state.usersReducer.user?.currency,
    selectedEvent: state.eventsReducer.selectedEvent,
    loading: state.eventsReducer.loading,
    eventsFetched: state.eventsReducer.eventsFetched,
    loadingSharedWithMe: state.eventsReducer.loadingSharedWithMe,
});

const mapDispatchToProps = {
    getAllEvents,
    getEventById,
    getUser,
    deleteEvent,
    deselectEvent,
    updateEvent,
    createEvent,
    getEventSharedWithMe,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EventContainer));
